import {
  useBrokersServiceBrokersTeamsAddUsersCreate,
  useBrokersServiceBrokersTeamsCreate,
  useBrokersServiceBrokersTeamsListKey,
  useBrokersServiceBrokersTeamsPartialUpdate,
  useBrokersServiceBrokersTeamsRemoveUserCreate,
  useBrokersServiceBrokersTeamsRetrieveKey
} from '@insurance/api/queries'
import { BrokerTeam } from '@insurance/api/requests'
import { BrokerUserSelect } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { getFullName, showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { App, Form, Input, Modal } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { TeamPermissionsSwitch } from './TeamPermissionsSwitch'
import { CommonPropsTeam } from './types'

export const EditAddTeam = ({
  data,
  broker,
  onSuccess,
  ...props
}: CommonPropsTeam) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const { required } = useValidationsField()

  useEffect(() => {
    if (!data) return form.resetFields()

    form.setFieldsValue({
      ...data,
      permissions: data.permissions?.map(({ permission }) => permission),
      __users: data.users.map(({ id, ...user }) => ({
        value: String(id),
        label: getFullName(user)
      }))
    })
  }, [data, form, props.open])

  const onSuccessRequest = () => {
    queryClient.invalidateQueries({
      queryKey: [useBrokersServiceBrokersTeamsListKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useBrokersServiceBrokersTeamsRetrieveKey, { id: data?.id }]
    })

    message.success(data ? t('edited_successfully') : t('added_successfully'))

    onSuccess()
  }

  const { mutateAsync: addUserMutate, isPending: isAddUserPending } =
    useBrokersServiceBrokersTeamsAddUsersCreate({
      onError: (error) => showApiErrors(error)
    })

  const { mutateAsync: removeUserMutate, isPending: isRemoveUserPending } =
    useBrokersServiceBrokersTeamsRemoveUserCreate({
      onError: (error) => showApiErrors(error)
    })

  const { mutateAsync: mutateEdit, isPending: isEditPending } =
    useBrokersServiceBrokersTeamsPartialUpdate({
      onError: (error) => showApiErrors(error, form)
    })

  const { mutateAsync: mutateCreate, isPending: isCreatePending } =
    useBrokersServiceBrokersTeamsCreate({
      onError: (error) => showApiErrors(error, form)
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const dataValues: BrokerTeam = {
      ...values,
      permissions: values?.permissions?.map((permission: string) => ({
        permission
      })),
      broker,
      __users: undefined
    }

    const addedUsers = values.__users
      ?.map(({ value }: { value: string }) => Number(value))
      ?.filter(
        (value: number) => !data?.users?.some(({ id }) => Number(id) === value)
      )

    const deletedUsers = data?.users
      ?.map(({ id }) => Number(id))
      ?.filter(
        (id) =>
          !values.__users?.some(
            ({ value }: { value: string }) => Number(value) === id
          )
      )

    let dataId = data?.id

    if (data?.id) {
      await mutateEdit({ id: data.id, requestBody: dataValues })
    } else {
      const data = await mutateCreate({ requestBody: dataValues })
      dataId = data?.id
    }

    if (addedUsers?.length && dataId)
      await addUserMutate({ id: dataId, requestBody: { users: addedUsers } })

    if (deletedUsers?.length && dataId)
      await removeUserMutate({
        id: dataId,
        requestBody: { users: deletedUsers }
      })

    onSuccessRequest()
  }

  const isPending =
    isEditPending || isCreatePending || isAddUserPending || isRemoveUserPending

  return (
    <Modal
      title={data ? t('edit_team') : t('add_team')}
      onOk={onSubmit}
      okButtonProps={{ loading: isPending }}
      width={800}
      {...props}
    >
      <Form layout="vertical" form={form}>
        <Form.Item name="name" label={t('name')} rules={[required]}>
          <Input placeholder={t('name')} />
        </Form.Item>

        <Form.Item name="__users" label={t('agents')}>
          <BrokerUserSelect
            mode="multiple"
            className="w-full"
            broker={String(broker)}
            labelInValue
          />
        </Form.Item>

        <Form.Item name="permissions">
          <TeamPermissionsSwitch broker={broker} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
